import React from "react";
import { Link } from "react-router-dom";
import {
  Phone, MessageCircle, ArrowRight, Star, CheckCircle2, Sparkles, Wand2,
} from "lucide-react";
import Seo from "@/components/Seo";
import Breadcrumbs from "@/components/Breadcrumbs";
import FAQAccordion from "@/components/FAQAccordion";
import AppointmentForm from "@/components/AppointmentForm";
import { CLINIC } from "@/lib/clinicInfo";

const FUNCTIONAL = [
  { name: "Ptosis", desc: "Droopy upper eyelid — congenital, age-related or neurological. Surgical repair restores lid position and field of vision." },
  { name: "Entropion", desc: "Inward-turning lower lid causing lash-cornea friction. Lid-tightening surgery restores comfort and protects the cornea." },
  { name: "Ectropion", desc: "Outward-turning lower lid with tearing and irritation. Reconstructive tightening returns normal lid apposition." },
  { name: "Eyelid Lesions", desc: "Cysts, chalazia, styes and skin lesions — precise excision with minimal scarring by an eye-trained surgeon." },
  { name: "Eyelid Reconstruction", desc: "Post-trauma, post-tumour excision or congenital lid defects — reconstructed in layers to restore function and appearance." },
  { name: "Tear Duct Disorders", desc: "Chronic watering (epiphora) evaluated and treated — including DCR surgery for blocked lacrimal drainage." },
];

const AESTHETIC = [
  { name: "Upper Blepharoplasty", desc: "Removal of excess upper-lid skin (and fat where needed) to open the eyes and lighten a tired appearance." },
  { name: "Lower Blepharoplasty", desc: "Correction of lower-lid bags via a transconjunctival (scarless) or trans-cutaneous approach — no visible external scar with the scarless technique." },
  { name: "Eye Bags", desc: "Under-eye puffiness from herniated fat and/or hollowing — treated surgically or with volume restoration depending on anatomy." },
  { name: "Periocular Ageing", desc: "Precision botulinum toxin injections for crow's feet and glabellar lines, hyaluronic-acid fillers for tear-troughs, and skin-tightening options." },
  { name: "Eyelid Asymmetry", desc: "Uneven eyelid position or crease — carefully assessed and corrected with millimetric surgical planning." },
];

const WHY = [
  { t: "Fellowship-Trained Oculoplasty", d: "LVPEI Hyderabad fellowship + aesthetic training under Dr. Ramtin Kassir, New York — inches from your eye, precision matters." },
  { t: "Eye-Safe Aesthetics", d: "Every filler and botulinum toxin placement respects the delicate periocular anatomy — natural results, not frozen or overdone." },
  { t: "Functional & Cosmetic Under One Roof", d: "Whether it's a ptosis repair or a lower blepharoplasty, both are performed with the same surgical rigour." },
  { t: "Honest Consultation", d: "If a non-surgical option will get you 80% of the result, we say so. If surgery is the right answer, we explain exactly why." },
];

const FAQS = [
  { q: "Do I need a surgery, filler or botulinum toxin injection?", a: "It depends on the anatomy — hollowing responds to filler, dynamic wrinkles to botulinum toxin, and true skin/fat excess needs surgery. A one-time assessment gives you a clear personalised plan." },
  { q: "How much time off work will I need after eyelid surgery?", a: "Most patients return to office work in 7–10 days with sunglasses. Visible swelling settles at 2–4 weeks; final result matures by 4–6 weeks." },
  { q: "Is oculoplasty covered by insurance?", a: "Functional procedures (ptosis, entropion, ectropion, DCR, reconstruction) are typically covered by health insurance. Purely aesthetic procedures are self-pay." },
  { q: "How natural do the results look?", a: "Because these procedures are done by an eye-trained specialist with an aesthetic sensibility, results look refreshed — not surgical. That is the entire point of choosing oculoplasty over a general cosmetic surgeon." },
];

export default function OculoplastyLanding() {
  return (
    <>
      <Seo
        title="Eyelid & Oculoplasty in Noida — Functional & Aesthetic | Dr. Gurkiran Kaur"
        description="Fellowship-trained oculoplasty in Sector 32 Noida — ptosis, entropion, ectropion, eyelid reconstruction, blepharoplasty, botulinum toxin injections, tear-trough fillers and periocular aesthetics by Dr. Gurkiran Kaur."
        path="/eyelid-oculoplasty"
      />
      <Breadcrumbs items={[{ name: "Eyelid & Oculoplasty", path: "/eyelid-oculoplasty" }]} />

      {/* HERO */}
      <section className="container-wide pt-8 pb-14">
        <div className="grid lg:grid-cols-12 gap-10 items-start">
          <div className="lg:col-span-7">
            <div className="text-xs uppercase tracking-widest text-accent mb-3">Eyelid & Oculoplasty in Noida</div>
            <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-foreground leading-[1.05]">
              Inches from your eye, <span className="text-primary italic">precision matters.</span>
            </h1>
            <p className="mt-6 text-base sm:text-lg text-foreground/75 max-w-xl leading-7">
              Functional eyelid surgery and periocular aesthetics in Sector 32 Noida, personally performed by <strong className="text-foreground">Dr. Gurkiran Kaur</strong> — fellowship-trained in oculoplasty and aesthetic surgery.
            </p>
            <div className="mt-6 flex flex-wrap items-center gap-4 text-sm text-foreground/70">
              <span className="flex items-center gap-1 text-amber-500">
                {Array.from({ length: 5 }).map((_, i) => <Star key={i} className="h-4 w-4 fill-current" />)}
              </span>
              <span><strong className="text-foreground">{CLINIC.ratingValue}</strong> · {CLINIC.reviewCount}+ Google reviews</span>
            </div>
            <div className="mt-8 flex flex-wrap gap-3">
              <a href="#book" className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-6 py-3 text-sm hover:bg-primary/90" data-testid="oculo-book-hero">
                Book Private Consultation <ArrowRight className="h-4 w-4" />
              </a>
              <a href={`tel:${CLINIC.phoneRaw}`} className="inline-flex items-center gap-2 rounded-full border border-border px-6 py-3 text-sm hover:bg-secondary" data-testid="oculo-call-hero">
                <Phone className="h-4 w-4" /> Call {CLINIC.phone}
              </a>
              <a href={CLINIC.whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 rounded-full px-4 py-3 text-sm text-primary" data-testid="oculo-wa-hero">
                <MessageCircle className="h-4 w-4" /> WhatsApp
              </a>
            </div>
          </div>
          <aside className="lg:col-span-5">
            <div className="rounded-2xl border border-border bg-card p-6 sm:p-7">
              <div className="text-xs uppercase tracking-widest text-accent mb-2">Private enquiry</div>
              <div className="font-serif text-xl mb-4">Book an oculoplasty consultation</div>
              <AppointmentForm defaultService="Oculoplasty & Eyelid Surgery in Noida" />
            </div>
          </aside>
        </div>
      </section>

      {/* FUNCTIONAL / AESTHETIC SPLIT */}
      <section className="border-y border-border bg-secondary/40 py-16" data-testid="oculo-split">
        <div className="container-wide">
          <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
            <div>
              <div className="text-xs uppercase tracking-widest text-accent mb-3">Two paths, one specialist</div>
              <h2 className="font-serif text-3xl sm:text-4xl text-foreground">Functional &amp; Aesthetic care.</h2>
            </div>
            <Link
              to="/eyelid-oculoplasty/guide"
              className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-primary/90"
              data-testid="oculoplasty-guide-link"
            >
              Download Your Oculoplasty Guide (PDF) <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
          <div className="grid md:grid-cols-2 gap-8">
          {/* FUNCTIONAL */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-primary/10 text-primary flex items-center justify-center"><Sparkles className="h-5 w-5" /></div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-accent">Functional</div>
                <div className="font-serif text-2xl text-foreground">Medical eyelid & tear surgery</div>
              </div>
            </div>
            <div className="space-y-3" data-testid="oculo-functional-list">
              {FUNCTIONAL.map((c) => (
                <div key={c.name} className="rounded-xl border border-border bg-card p-5" data-testid={`func-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="font-serif text-base text-foreground">{c.name}</div>
                  <p className="text-sm text-foreground/70 leading-6 mt-1">{c.desc}</p>
                </div>
              ))}
            </div>
          </div>
          {/* AESTHETIC */}
          <div>
            <div className="flex items-center gap-3 mb-4">
              <div className="h-10 w-10 rounded-full bg-accent/15 text-accent flex items-center justify-center"><Wand2 className="h-5 w-5" /></div>
              <div>
                <div className="text-[11px] uppercase tracking-widest text-accent">Aesthetic</div>
                <div className="font-serif text-2xl text-foreground">Periocular aesthetics</div>
              </div>
            </div>
            <div className="space-y-3" data-testid="oculo-aesthetic-list">
              {AESTHETIC.map((c) => (
                <div key={c.name} className="rounded-xl border border-border bg-card p-5" data-testid={`aesth-${c.name.toLowerCase().replace(/\s+/g, "-")}`}>
                  <div className="font-serif text-base text-foreground">{c.name}</div>
                  <p className="text-sm text-foreground/70 leading-6 mt-1">{c.desc}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
        </div>
      </section>

      {/* WHY */}
      <section className="container-wide py-16" data-testid="oculo-why">
        <div className="text-xs uppercase tracking-widest text-accent mb-3">Why Nehchal</div>
        <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-8">Why choose an oculoplastic surgeon.</h2>
        <div className="grid md:grid-cols-2 gap-4">
          {WHY.map((w) => (
            <div key={w.t} className="rounded-xl border border-border bg-card p-6">
              <div className="font-serif text-lg text-foreground">{w.t}</div>
              <p className="mt-2 text-sm text-foreground/75 leading-6">{w.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* FAQs */}
      <FAQAccordion faqs={FAQS} title="Eyelid & oculoplasty — your questions answered" />

      {/* BOOK */}
      <section id="book" className="bg-secondary/40 border-t border-border py-20" data-testid="oculo-book">
        <div className="container-wide grid lg:grid-cols-2 gap-10">
          <div>
            <div className="text-xs uppercase tracking-widest text-accent mb-3">Book Consultation</div>
            <h2 className="font-serif text-3xl sm:text-4xl text-foreground mb-4">A private, one-to-one evaluation.</h2>
            <p className="text-foreground/70 leading-7 mb-6">
              Every oculoplasty plan is personal. Book a private consultation with Dr. Kaur — surgical or non-surgical, we will explain every honest option.
            </p>
            <a href={`tel:${CLINIC.phoneRaw}`} className="inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground px-5 py-2.5 text-sm hover:bg-primary/90">
              <Phone className="h-4 w-4" /> Call {CLINIC.phone}
            </a>
          </div>
          <div className="rounded-2xl border border-border bg-card p-6 sm:p-8">
            <AppointmentForm defaultService="Oculoplasty & Eyelid Surgery in Noida" />
          </div>
        </div>
      </section>
    </>
  );
}
